import { useState, type FormEvent } from 'react'
import {
  ApiError,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES_HINT,
  describePasswordProblem,
  type Passkey,
  type User,
} from '@mads/shared'
import { MyAuditLog } from '@/components/AuditLog'
import { ConfirmDialog, ErrorNote, Loading } from '@/components/Feedback'
import { describeDays } from '@/lib/duration'
import { PasskeyCancelled, passkeysSupported } from '@/lib/passkeys'
import {
  useChangePassword,
  useForgetTrustedDevices,
  usePasskeys,
  useRegisterPasskey,
  useRemovePasskey,
  useTwoFactor,
} from '@/lib/queries'
import { useCurrentUser } from '@/lib/session'

/**
 * Where someone maintains their own account without an admin doing it for them:
 * their password, and the passkeys on it.
 *
 * The record itself is read-only here (issue #52) — the name as much as the
 * email address and the phone number. The theatre's account list is the Head of
 * Tech's to keep straight, and a name people are rostered and messaged under is
 * part of it, so all three are the admin's through `/users` (spec W-05). They
 * are shown disabled with the reason beside them rather than left out, which
 * would only prompt the question of where they had gone.
 */
export function Profile() {
  const user = useCurrentUser()

  if (user === null) return <Loading what="Loading your account" />

  // Remounts if the signed-in account changes, so the fields start from theirs.
  return (
    <>
      <h1 className="page-title">Your account</h1>
      <p className="page-lede">
        Your password and how you sign in. The rest of your account &mdash; your name and contact
        details, the access you have, and whether you have any &mdash; is set by an admin.
      </p>

      <Details key={`details-${user.id}`} user={user} />
      <PasswordForm key={`password-${user.id}`} />
      <PasskeysSection key={`passkeys-${user.id}`} />
      <TwoStepSection key={`two-step-${user.id}`} />
      <MyAuditLog key={`activity-${user.id}`} />
    </>
  )
}

/**
 * What the theatre holds about you, and nothing to submit: every field here is
 * an admin's to change (issue #52), so the section is a rendering of the record
 * rather than a form that would have to refuse what was typed into it.
 */
function Details({ user }: { user: User }) {
  return (
    <section style={{ maxWidth: 520 }}>
      <h2 className="section-title" style={{ marginTop: 0 }}>
        Your details
      </h2>

      <div className="field">
        <label htmlFor="profile-name">Name</label>
        <input id="profile-name" className="input" value={user.name} readOnly disabled />
        <span className="text-muted" style={{ fontSize: 12 }}>
          The name you appear under on the inventory. Ask the Head of Tech to change it.
        </span>
      </div>

      <div className="field">
        <label htmlFor="profile-email">Email</label>
        <input id="profile-email" className="input" value={user.email} readOnly disabled />
        <span className="text-muted" style={{ fontSize: 12 }}>
          This is how you sign in. Ask the Head of Tech to change it.
        </span>
      </div>

      <div className="field">
        <label htmlFor="profile-phone">Phone number</label>
        <input
          id="profile-phone"
          className="input"
          placeholder="None on your account"
          value={user.phone ?? ''}
          readOnly
          disabled
        />
        <span className="text-muted" style={{ fontSize: 12 }}>
          {user.phone === null
            ? 'The theatre holds no number for you, which is your choice to make. Ask the Head of Tech to add one if you’d like a code texted to you when you sign in.'
            : 'The number the theatre holds for you. Ask the Head of Tech to change or remove it.'}
        </span>
      </div>
    </section>
  )
}

/**
 * The current password is asked for because a signed-in browser someone walked
 * away from should not be enough to change the password on it. Anyone who has
 * forgotten theirs goes through the emailed link instead.
 */
function PasswordForm() {
  const changePassword = useChangePassword()
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const error = validationError ?? readError(changePassword.error)

  function edit(set: (value: string) => void) {
    return (event: { target: { value: string } }) => {
      set(event.target.value)
      // Typing again means they're not looking at the last result any more.
      if (changePassword.isSuccess) changePassword.reset()
      setValidationError(null)
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    // Length only; the blocklist half of the policy (issue #18) is the API's,
    // and comes back as a plain error above the form.
    const tooWeak = describePasswordProblem(password)

    if (tooWeak !== null) {
      setValidationError(tooWeak)
      return
    }

    if (password !== confirm) {
      setValidationError('Those passwords don’t match.')
      return
    }

    setValidationError(null)
    changePassword.mutate(
      { currentPassword: current, password },
      {
        onSuccess: () => {
          setCurrent('')
          setPassword('')
          setConfirm('')
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 520, marginTop: 'var(--space-8)' }}>
      <h2 className="section-title" style={{ marginTop: 0 }}>
        Password
      </h2>

      {error !== null && (
        <div className="error-note" role="alert">
          {error}
        </div>
      )}

      {changePassword.isSuccess && (
        <p className="confirm-note" role="status">
          Your password has been changed.
        </p>
      )}

      <div className="field">
        <label htmlFor="current-password">Current password</label>
        <input
          id="current-password"
          className="input"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={edit(setCurrent)}
        />
      </div>

      <div className="field">
        <label htmlFor="new-password">New password</label>
        <input
          id="new-password"
          className="input"
          type="password"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          required
          value={password}
          onChange={edit(setPassword)}
        />
        <span className="text-muted" style={{ fontSize: 12 }}>
          {PASSWORD_RULES_HINT}
        </span>
      </div>

      <div className="field">
        <label htmlFor="confirm-password">Confirm new password</label>
        <input
          id="confirm-password"
          className="input"
          type="password"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          required
          value={confirm}
          onChange={edit(setConfirm)}
        />
      </div>

      <div className="action-row" style={{ marginTop: 'var(--space-4)' }}>
        <button type="submit" className="btn btn-primary" disabled={changePassword.isPending}>
          {changePassword.isPending ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  )
}

/**
 * Passkeys (issue #15) — the account holder's second factor, theirs to add and
 * theirs to give up.
 *
 * Having any at all is what turns 2FA on, so the list is the whole state:
 * nothing is toggled, and the section says which of the two situations you are
 * in rather than showing a switch that would only ever agree with the rows
 * below it.
 *
 * Both the adding and the removing ask for the current password, for the reason
 * the password form already does — a browser somebody walked away from must not
 * be enough to change what signs its owner in. Adding matters as much as
 * removing here: a passkey quietly enrolled from a borrowed session would be a
 * second factor belonging to whoever enrolled it.
 */
function PasskeysSection() {
  const passkeys = usePasskeys()
  const register = useRegisterPasskey()
  const remove = useRemovePasskey()

  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Passkey | null>(null)
  const supported = passkeysSupported()

  if (passkeys.isLoading) return <Loading what="Loading your passkeys" />

  const registered = passkeys.data ?? []

  return (
    <section style={{ maxWidth: 520, marginTop: 'var(--space-8)' }}>
      <h2 className="section-title" style={{ marginTop: 0 }}>
        Passkeys
      </h2>

      <p className="text-muted" style={{ fontSize: 13 }}>
        {registered.length === 0
          ? 'Add a passkey and you’ll be asked for it after your password every time you sign in — a fingerprint, a face, a PIN or a security key, depending on the device.'
          : 'You’ll be asked for one of these after your password every time you sign in. Signing in with Google skips this step.'}
      </p>

      <ErrorNote error={passkeys.error} />

      {registered.length > 0 && (
        <ul className="passkey-list">
          {registered.map((passkey) => (
            <li key={passkey.id} className="passkey-row">
              <div>
                <strong>{passkey.label}</strong>
                <div className="text-muted passkey-row-meta">
                  Added {formatDate(passkey.createdAt)}
                  {passkey.lastUsedAt === null
                    ? ' · not used yet'
                    : ` · last used ${formatDate(passkey.lastUsedAt)}`}
                </div>
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => setRemoving(passkey)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {supported ? (
        <div className="action-row" style={{ marginTop: 'var(--space-4)' }}>
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            Add a passkey
          </button>
        </div>
      ) : (
        <p className="text-muted" style={{ fontSize: 12, marginTop: 'var(--space-4)' }}>
          This browser can&rsquo;t create passkeys. Open the inventory on a device that can, and
          add one there.
        </p>
      )}

      {adding && (
        <AddPasskeyDialog
          busy={register.isPending}
          error={register.error}
          onCancel={() => {
            register.reset()
            setAdding(false)
          }}
          onConfirm={(currentPassword, label) =>
            register.mutate(
              { currentPassword, label },
              {
                onSuccess: () => {
                  register.reset()
                  setAdding(false)
                },
              },
            )
          }
        />
      )}

      {removing !== null && (
        <RemovePasskeyDialog
          passkey={removing}
          busy={remove.isPending}
          error={remove.error}
          onCancel={() => {
            remove.reset()
            setRemoving(null)
          }}
          onConfirm={(currentPassword) =>
            remove.mutate(
              { id: removing.id, currentPassword },
              {
                onSuccess: () => {
                  remove.reset()
                  setRemoving(null)
                },
              },
            )
          }
        />
      )}
    </section>
  )
}

/**
 * The texted code (issue #16), and the browsers that have been told to stop
 * asking for it.
 *
 * Read-only apart from the one control that matters, because there is nothing
 * here to switch: whether an account is texted follows from having a mobile
 * number on an installation with Twilio switched on, and both of those belong
 * to the Head of Tech. What this section is for is saying which of those
 * situations you are in — including the one where nothing is protecting the
 * account but its password, which is worth being told plainly rather than
 * leaving to be inferred from silence.
 *
 * A registered passkey takes precedence over the code, so the two are never
 * asked for together and this says so where it applies.
 */
function TwoStepSection() {
  const status = useTwoFactor()
  const passkeys = usePasskeys()
  const forget = useForgetTrustedDevices()
  const [forgetting, setForgetting] = useState(false)

  if (status.isLoading) return <Loading what="Loading two-step verification" />

  if (status.data === undefined) {
    return (
      <section style={{ maxWidth: 520, marginTop: 'var(--space-8)' }}>
        <h2 className="section-title" style={{ marginTop: 0 }}>
          Two-step verification
        </h2>
        <ErrorNote error={status.error} />
      </section>
    )
  }

  const { smsConfigured, smsAvailable, phoneHint, trustedDevices, trustedDeviceDays } = status.data
  const hasPasskey = (passkeys.data ?? []).length > 0

  return (
    <section style={{ maxWidth: 520, marginTop: 'var(--space-8)' }}>
      <h2 className="section-title" style={{ marginTop: 0 }}>
        Two-step verification
      </h2>

      {/* Nothing here can fail outside the dialog, and the one thing that can
          — a wrong password — is a violation on `currentPassword`, which
          `<ErrorNote>` would render as "Validation failed" above a field name.
          `<DialogProblem>` says it in the dialog instead. */}

      <p className="text-muted" style={{ fontSize: 13 }}>
        {!smsConfigured
          ? 'Text-message verification isn’t set up on this installation. Add a passkey above if you want a second step on your account.'
          : hasPasskey
            ? `You’ll be asked for a passkey after your password, so no code is texted to you. Remove your last passkey and we’d text ${phoneHint ?? 'your mobile'} instead.`
            : smsAvailable
              ? `After your password we’ll text a code to the number ending ${phoneHint}. Add a passkey above and you’ll be asked for that instead.`
              : 'There’s no mobile number on your account, so no code can be texted to you and your password is all that’s needed to sign in. Ask the Head of Tech to add one, or add a passkey above.'}
      </p>

      {smsAvailable && !hasPasskey && (
        <>
          <p style={{ fontSize: 14 }}>
            {trustedDevices === 0
              ? 'No browsers are currently trusted to skip that step.'
              : `${trustedDevices} browser${trustedDevices === 1 ? ' is' : 's are'} trusted to skip that step. Trust lasts ${describeDays(trustedDeviceDays)} unless you end it here, and changing your password ends it everywhere.`}
          </p>

          {trustedDevices > 0 && (
            <div className="action-row" style={{ marginTop: 'var(--space-4)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setForgetting(true)}>
                Forget trusted devices
              </button>
            </div>
          )}
        </>
      )}

      {forgetting && (
        <ForgetDevicesDialog
          count={trustedDevices}
          busy={forget.isPending}
          error={forget.error}
          onCancel={() => {
            forget.reset()
            setForgetting(false)
          }}
          onConfirm={(currentPassword) =>
            forget.mutate(currentPassword, {
              onSuccess: () => {
                forget.reset()
                setForgetting(false)
              },
            })
          }
        />
      )}
    </section>
  )
}

/**
 * Destructive in the sense that matters — it puts a step back in front of
 * people — so it confirms first (spec W-03), with the password inside the
 * dialog the way passkey removal does.
 */
function ForgetDevicesDialog({
  count,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  count: number
  busy: boolean
  error: unknown
  onCancel: () => void
  onConfirm: (currentPassword: string) => void
}) {
  const [currentPassword, setCurrentPassword] = useState('')

  return (
    <ConfirmDialog
      title="Forget trusted devices?"
      body={`All ${count} will be asked for a texted code again next time they sign in, including this one. Do this if a device has been lost or is no longer yours.`}
      confirmLabel="Forget them"
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(currentPassword)}
    >
      <div className="dialog-body">
        <DialogProblem error={error} />

        <div className="field">
          <label htmlFor="forget-devices-password">Your password</label>
          <input
            id="forget-devices-password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>
      </div>
    </ConfirmDialog>
  )
}

/**
 * The browser's own prompt appears once this is confirmed, which is why the
 * name is asked for up front: afterwards the person is looking at a fingerprint
 * reader, not at this form.
 */
function AddPasskeyDialog({
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  busy: boolean
  error: unknown
  onCancel: () => void
  onConfirm: (currentPassword: string, label: string) => void
}) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [label, setLabel] = useState('')

  return (
    <ConfirmDialog
      title="Add a passkey"
      body="Your device will ask you to confirm. Give the passkey a name so you can tell it from any others, and enter your password."
      confirmLabel="Add passkey"
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(currentPassword, label.trim())}
    >
      <div className="dialog-body">
        <DialogProblem error={error} />

        <div className="field">
          <label htmlFor="passkey-label">Passkey name</label>
          <input
            id="passkey-label"
            className="input"
            maxLength={60}
            placeholder="e.g. my phone"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="passkey-password">Your password</label>
          <input
            id="passkey-password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>
      </div>
    </ConfirmDialog>
  )
}

function RemovePasskeyDialog({
  passkey,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  passkey: Passkey
  busy: boolean
  error: unknown
  onCancel: () => void
  onConfirm: (currentPassword: string) => void
}) {
  const [currentPassword, setCurrentPassword] = useState('')

  return (
    <ConfirmDialog
      title={`Remove “${passkey.label}”?`}
      body="You won’t be able to sign in with this passkey again. If it’s your last one, you’ll go back to signing in with your password alone."
      confirmLabel="Remove passkey"
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(currentPassword)}
    >
      <div className="dialog-body">
        <DialogProblem error={error} />

        <div className="field">
          <label htmlFor="remove-passkey-password">Your password</label>
          <input
            id="remove-passkey-password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>
      </div>
    </ConfirmDialog>
  )
}

/**
 * A wrong password comes back as a violation on `currentPassword`, and a
 * dismissed browser prompt is not an API error at all — both need saying in the
 * dialog rather than through `<ErrorNote>`, which would print "Validation
 * failed" above a field name.
 */
function DialogProblem({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null

  const message =
    error instanceof PasskeyCancelled
      ? 'Your device didn’t confirm. Try again.'
      : (readError(error) ?? 'Something went wrong.')

  return (
    <div className="error-note" role="alert">
      {message}
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * A wrong current password comes back as a field violation. Shown on its own
 * rather than through `<ErrorNote>`, which would print the generic "Validation
 * failed" above a `currentPassword:` line — accurate, and no way to say it.
 */
function readError(error: unknown): string | null {
  if (error === null || error === undefined) return null

  if (error instanceof ApiError) {
    return error.violations.currentPassword ?? error.message
  }

  return error instanceof Error ? error.message : 'Something went wrong.'
}
